import './App.css';
import * as React from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, Navigate, Routes, Route, useLocation } from "react-router-dom";
import Dashboard from './pages/Dashboard';
import Moderation from './pages/Moderation';
import Users from './pages/Users';
import Flagged from './pages/Flagged';
import Loras from './pages/Loras';
import Models from './pages/Models';
import NotFound from './pages/NotFound';
import Runpod from './pages/Runpod';
import SignIn from './pages/SignIn';
import Layout from './pages/Layout';
import Alerts from './pages/Alerts';
import { EnvironmentProvider, useEnvironment } from './components/useEnvironment';
import { ThemeProvider, useTheme } from './components/ThemeContext';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

const PrivateRoute = (props) => {
    const { children } = props
    const isLoggedIn = localStorage.getItem('logged_user') !== null;
    const location = useLocation()

    return isLoggedIn ? (
        <>{children}</>
    ) : (
        <Navigate
            replace={true}
            to="/signin"
            state={{ from: `${location.pathname}${location.search}` }}
        />
    )
}

const AppContent = () => {
    const { environment } = useEnvironment();
    const { theme: themeMode } = useTheme();

    React.useEffect(() => {
        const envSuffix = environment === 'development' ? 'dev' : 'prod';
        document.title = `Diffie Central - ${envSuffix}`;
    }, [environment]);

    const theme = React.useMemo(
        () =>
            createTheme({
                palette: {
                    mode: themeMode,
                    ...(themeMode === 'light'
                        ? {
                            // Light mode colors
                            primary: {
                                main: '#1976d2',
                            },
                            secondary: {
                                main: '#dc004e',
                            },
                            background: {
                                default: '#f5f5f5',
                                paper: '#ffffff',
                            },
                        }
                        : {
                            // Dark mode colors - pleasant deep blue theme with high contrast
                            primary: {
                                main: '#29b6f6',
                                light: '#73e8ff',
                                dark: '#0086c3',
                            },
                            secondary: {
                                main: '#ce93d8',
                                light: '#ffc4ff',
                                dark: '#9c64a6',
                            },
                            background: {
                                default: '#0a1929',
                                paper: '#1e2a35', // Lighter for better contrast
                            },
                            text: {
                                primary: '#ffffff',
                                secondary: 'rgba(255, 255, 255, 0.7)',
                            },
                        }),
                },
                components: {
                    MuiTypography: {
                        styleOverrides: {
                            body1: {
                                // color: 'rgba(255, 255, 255, 0.50)'
                            }
                        }
                    },
                    MuiGrid2: {
                        styleOverrides: {
                            root: {
                                backgroundColor: 'transparent'
                            },
                        },
                    },
                    MuiAppBar: {
                        styleOverrides: {
                            root: {
                                backgroundImage: 'none',
                            },
                        },
                    },
                    MuiCard: {
                        styleOverrides: {
                            root: {
                                backgroundImage: 'none',
                                ...(themeMode === 'dark' && {
                                    boxShadow: '0 4px 6px rgba(255, 255, 255, 0.1), 0 1px 3px rgba(255, 255, 255, 0.08)',
                                }),
                            },
                        },
                    },
                    MuiTextField: {
                        styleOverrides: {
                            root: {
                                ...(themeMode === 'dark' && {
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#0d1b2a',
                                        '& fieldset': {
                                            borderColor: 'rgba(255, 255, 255, 0.23)',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(255, 255, 255, 0.4)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#29b6f6',
                                        },
                                    },
                                }),
                            },
                        },
                    },
                    MuiOutlinedInput: {
                        styleOverrides: {
                            root: {
                                ...(themeMode === 'dark' && {
                                    backgroundColor: '#0d1b2a',
                                    '& fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.23)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.4)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#29b6f6',
                                    },
                                }),
                            },
                        },
                    },
                    MuiInputBase: {
                        styleOverrides: {
                            root: {
                                ...(themeMode === 'dark' && {
                                    backgroundColor: '#0d1b2a',
                                }),
                            },
                        },
                    },
                },
            }),
        [themeMode],
    );

    return (
        <MuiThemeProvider theme={theme}>
            <CssBaseline />
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Layout />} >
                        <Route index element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                        <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                        <Route path="/moderation" element={<PrivateRoute><Moderation /></PrivateRoute>} />
                        <Route path="/users" element={<PrivateRoute><Users /></PrivateRoute>} />
                        <Route path="/models" element={<PrivateRoute><Models /></PrivateRoute>} />
                        <Route path="/loras" element={<PrivateRoute><Loras /></PrivateRoute>} />
                        <Route path="/alerts" element={<PrivateRoute><Alerts /></PrivateRoute>} />
                        <Route path="/flagged" element={<PrivateRoute><Flagged /></PrivateRoute>} />
                        <Route path="/runpod" element={<PrivateRoute><Runpod /></PrivateRoute>} />
                    </Route>
                    <Route path="/signin" element={<SignIn />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </BrowserRouter>
        </MuiThemeProvider>
    );
}

function App() {
    return (
        <ThemeProvider>
            <EnvironmentProvider>
                <AppContent />
            </EnvironmentProvider>
        </ThemeProvider>
    );
}

export default App;
